import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

import { channel as $channelContent } from '../content';
import { useUser } from './User';
import useContextHook from './useContextHook';
import { useChannel } from './Channel';
import { useNotif } from './Notification';
import useChatActions from '../pages/Channel/Chat/useChatConnection/useChatActions';
import { CHAT_LOG_LEVELS, MAX_RECONNECT_ATTEMPTS } from '../constants';
import {
  CHAT_USER_ROLE,
  requestChatToken
} from '../pages/Channel/Chat/useChatConnection/utils';
import { ChatRoom } from 'amazon-ivs-chat-messaging';
import { extractChannelIdfromChannelArn } from '../utils';
import { CHAT_MESSAGE_EVENT_TYPES } from '../constants';

const { SEND_MESSAGE } = CHAT_MESSAGE_EVENT_TYPES;
const $content = $channelContent.chat;
const { INFO: info, DEBUG: debug } = CHAT_LOG_LEVELS;
const isDebug = false;

const Context = createContext(null);
Context.displayName = 'Chat';

const actionTypes = {
  INIT_MESSAGES: 'INIT_MESSAGES',
  ADD_MESSAGE: 'ADD_MESSAGE',
  DELETE_MESSAGE: 'DELETE_MESSAGE',
  DELETE_MESSAGES_BY_USER_ID: 'DELETE_MESSAGES_BY_USER_ID',
  CLEAR_MESSAGES: 'CLEAR_MESSAGES' // New action type
};

const reducer = (messages, action) => {
  const messageMap = new Map(messages.map((message) => [message.id, message]));

  switch (action.type) {
    case actionTypes.INIT_MESSAGES: {
      // Don't clear on reconnect, only set initial messages if provided
      if (action.initialMessages) {
        action.initialMessages.forEach((message) => {
          messageMap.set(message.id, { ...message, isPreloaded: true });
        });
      }
      break;
    }
    case actionTypes.CLEAR_MESSAGES: {
      messageMap.clear(); // Only clear when explicitly requested
      break;
    }
    case actionTypes.ADD_MESSAGE: {
      const { message: newMessage, isOwnMessage } = action;
      messageMap.set(newMessage.id, { ...newMessage, isOwnMessage });
      break;
    }
    case actionTypes.DELETE_MESSAGE: {
      const { messageId: messageIdToDelete, deletedMessageIds } = action;
      const wasDeletedByUser =
        deletedMessageIds.current.includes(messageIdToDelete);
      const messageToDelete = messageMap.get(messageIdToDelete);
      messageMap.set(messageIdToDelete, {
        ...messageToDelete,
        isDeleted: true,
        wasDeletedByUser
      });
      break;
    }
    case actionTypes.DELETE_MESSAGES_BY_USER_ID: {
      const { userId: userIdToDelete } = action;
      for (const [id, message] of messageMap) {
        if (message.sender.attributes.channelArn === userIdToDelete) {
          messageMap.delete(id);
        }
      }
      break;
    }
    default:
      throw new Error('Unexpected action type');
  }

  return Array.from(messageMap.values());
};

export const Provider = ({ children }) => {
  /** @type {[Messages, Function]} */
  const [messages, dispatch] = useReducer(reducer, []);
  const persistentMessages = useRef(new Map()); // Store messages persistently
  const [isOnline, setIsOnline] = useState(window.navigator.onLine);
  const [wasManuallyDisconnected, setWasManuallyDisconnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);

  const sentMessageIds = useRef([]);
  const deletedMessageIds = useRef([]);
  const { userData } = useUser();
  const { sub: ownUsername, userRole } = userData || {};
  const { channelData } = useChannel();
  const { isViewerBanned = false } = channelData || {};
  const chatRoomOwnerUsername = ownUsername;
  const { notifyError, dismissNotif } = useNotif();
  const retryConnectionAttemptsCounterRef = useRef(0);
  const chatCapabilities = useRef([]);

  // Connection State
  const [hasConnectionError, setHasConnectionError] = useState();
  const [sendAttemptError, setSendAttemptError] = useState();
  const connection = useRef(null);
  const [room, setRoom] = useState(null);
  const isConnectionOpenRef = useRef(false);

  const isInitializingConnection = useRef(false);
  // const abortControllerRef = useRef();
  const isConnecting = isInitializingConnection.current;

  // Chat Actions
  const [deletedMessage, setDeletedMessage] = useState();
  const { actions, chatUserRole, updateUserRole } = useChatActions({
    chatCapabilities,
    isConnectionOpen: isConnectionOpenRef.current,
    connection,
    setSendAttemptError
  });
  const isModerator = chatUserRole === CHAT_USER_ROLE.MODERATOR;

  const { pathname } = useLocation();

  const initMessages = useCallback(() => {
    if (!isReconnecting) {
      // Only clear messages if this is not a reconnection
      dispatch({
        type: actionTypes.CLEAR_MESSAGES
      });
    }
    // Initialize with any stored messages
    dispatch({
      type: actionTypes.INIT_MESSAGES,
      initialMessages: Array.from(persistentMessages.current.values())
    });
  }, [isReconnecting]);

  const addMessage = useCallback(
    (message) => {
      const isOwnMessage =
        ownUsername === (message.sender.userId || message.sender.UserId);
      if (isOwnMessage) sentMessageIds.current.push(message.id);

      const messageWithTime = {
        ...message,
        SendTime: message.SendTime || new Date()
      };

      // Store in persistent storage
      persistentMessages.current.set(messageWithTime.id, messageWithTime);

      dispatch({
        type: actionTypes.ADD_MESSAGE,
        message: messageWithTime,
        isOwnMessage
      });
    },
    [ownUsername]
  );

  const removeMessage = useCallback((messageId) => {
    persistentMessages.current.delete(messageId); // Remove from persistent storage
    dispatch({
      type: actionTypes.DELETE_MESSAGE,
      messageId,
      deletedMessageIds
    });
  }, []);

  const removeMessageByUserId = useCallback((userId) => {
    dispatch({
      type: actionTypes.DELETE_MESSAGES_BY_USER_ID,
      userId
    });
  }, []);

  // messages local state
  const handleDeleteMessage = useCallback(
    (messageId) => {
      removeMessage(messageId);
      setDeletedMessage(messageId);
    },
    [removeMessage]
  );

  const handleUserDisconnect = useCallback(
    (bannedUsername) => {
      const bannedUserChannelId =
        extractChannelIdfromChannelArn(bannedUsername);

      if (bannedUserChannelId === userData?.sub.toLowerCase()) {
        // This user has been banned
        notifyError($content.notifications.error.you_have_been_banned);
      }
    },
    [notifyError, userData?.sub]
  );

  const disconnect = useCallback(() => {
    if (connection.current) {
      try {
        connection.current.disconnect();
      } catch (err) {
        console.error('Error disconnecting:', err);
      }
    }
    setRoom(null);
    connection.current = null;
    chatCapabilities.current = null;
    isInitializingConnection.current = false;
    isConnectionOpenRef.current = false;
    setHasConnectionError(false);
  }, []);

  const connect = useCallback(() => {
    if (
      !isOnline ||
      isViewerBanned !== false ||
      !chatRoomOwnerUsername ||
      isInitializingConnection.current ||
      connection.current // Add check for existing connection
    )
      return;

    // Reset manual disconnect flag when explicitly connecting
    setWasManuallyDisconnected(false);

    // Clean up previous connection resources
    disconnect();

    // Small delay to ensure cleanup is complete
    setTimeout(() => {
      if (!isOnline) return; // Double check we're still online

      isInitializingConnection.current = true;
      setHasConnectionError(false);

      // create a new instance of chat room
      const room = new ChatRoom({
        regionOrUrl: process.env.REACT_APP_IVS_CHAT_REGION,
        maxReconnectAttempts: MAX_RECONNECT_ATTEMPTS,
        tokenProvider: async () => {
          const data = await requestChatToken(userRole);

          if (data?.error) {
            retryConnectionAttemptsCounterRef.current += 1;
            if (
              retryConnectionAttemptsCounterRef.current ===
              MAX_RECONNECT_ATTEMPTS
            ) {
              isInitializingConnection.current = false;
              notifyError($content.notifications.error.error_loading_chat, {
                withTimeout: false
              });
              setHasConnectionError(true);
            }
          } else {
            chatCapabilities.current = data.capabilities;
          }

          return {
            ...data,
            ...(!data?.error && {
              sessionExpirationTime: new Date(data.sessionExpirationTime)
            })
          };
        }
      });

      room.logLevel =
        process.env.REACT_APP_STAGE === 'prod' ? info : isDebug && debug;
      room.connect();
      setRoom(room);
      connection.current = room;
      isConnectionOpenRef.current = true;
      isInitializingConnection.current = false;
    }, 0);
  }, [
    isOnline,
    isViewerBanned,
    chatRoomOwnerUsername,
    disconnect,
    notifyError,
    userRole
  ]);

  // Network state management
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasManuallyDisconnected) return;
      setIsReconnecting(true); // Mark as reconnecting
      connect();
    };

    const handleOffline = () => {
      setIsOnline(false);
      if (connection.current) {
        disconnect();
        setWasManuallyDisconnected(true);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [connect, disconnect, wasManuallyDisconnected]);

  // Reset reconnecting flag after connection is established
  useEffect(() => {
    if (room && isConnectionOpenRef.current) {
      setIsReconnecting(false);
    }
  }, [room]);

  // Clear messages when stream goes offline
  useEffect(() => {
    if (!channelData?.isLive) {
      // Clear persistent storage
      persistentMessages.current.clear();
      // Clear displayed messages
      dispatch({ type: actionTypes.CLEAR_MESSAGES });
      // Reset message tracking
      sentMessageIds.current = [];
      deletedMessageIds.current = [];
    }
  }, [channelData?.isLive]);

  // Initialize connection
  useEffect(() => {
    connect();

    return disconnect;
  }, [connect, disconnect]);

  useEffect(() => {
    // If chat room listeners are not available, do not continue
    if (!room || !room.addListener) {
      return;
    }

    const unsubscribeOnConnect = room.addListener('connect', () => {
      updateUserRole();
      dismissNotif();
    });

    const unsubscribeOnDisconnect = room.addListener('disconnect', () => {
      isConnectionOpenRef.current = false;
      connection.current = null;
      setRoom(null);
      chatCapabilities.current = [];

      updateUserRole();
    });

    const unsubscribeOnUserDisconnect = room.addListener(
      'userDisconnect',
      (event) => {
        const { sub: trackingId } = userData;
        const { userId: bannedUserId } = event;

        handleUserDisconnect(bannedUserId);

        const bannedUserChannelId =
          extractChannelIdfromChannelArn(bannedUserId);
        if (bannedUserChannelId !== trackingId.toLowerCase()) {
          removeMessageByUserId(bannedUserId);
        }
      }
    );

    const unsubscribeOnMessage = room.addListener('message', (message) => {
      const {
        attributes: { eventType = undefined }
      } = message;
      switch (eventType) {
        case SEND_MESSAGE:
          addMessage(message);
          break;
        default:
          break;
      }
    });

    const unsubscribeOnMessageDelete = room.addListener(
      'messageDelete',
      (deletedMessage) => {
        const {
          attributes: { MessageID },
          reason
        } = deletedMessage;
        handleDeleteMessage(MessageID, reason);
      }
    );

    return () => {
      unsubscribeOnConnect();
      unsubscribeOnDisconnect();
      unsubscribeOnMessage();
      unsubscribeOnMessageDelete();
      unsubscribeOnUserDisconnect();
    };
  }, [
    addMessage,
    room,
    updateUserRole,
    dismissNotif,
    handleDeleteMessage,
    handleUserDisconnect,
    userData,
    removeMessageByUserId,
    isModerator,
    pathname,
    actions
  ]);

  // Add effect to re-initialize messages when room connection is established
  useEffect(() => {
    if (room && isConnectionOpenRef.current) {
      // When connection is established/re-established, initialize messages
      initMessages();
    }
  }, [room, initMessages]); // Remove isConnectionOpenRef.current from dependencies since it's mutable

  const value = useMemo(
    () => ({
      addMessage,
      deletedMessageIds,
      initMessages,
      messages,
      removeMessage,
      removeMessageByUserId,
      sentMessageIds,
      actions,
      chatUserRole,
      hasConnectionError,
      isConnecting,
      sendAttemptError,
      isModerator,
      deletedMessage,
      setDeletedMessage,
      isOnline // Add network state to context
    }),
    [
      actions,
      addMessage,
      chatUserRole,
      hasConnectionError,
      initMessages,
      isConnecting,
      messages,
      removeMessage,
      removeMessageByUserId,
      sendAttemptError,
      isModerator,
      deletedMessage,
      setDeletedMessage,
      isOnline
    ]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

Provider.propTypes = { children: PropTypes.node.isRequired };

export const useChat = () => useContextHook(Context);
